import { useState } from "react";
import { useStytchMember } from "@stytch/react/b2b";
import { getUserDbId } from "@/lib/models/auth";
import {
  useOverdueInstances,
  useUpcomingInstances,
  useListInstances,
  TaskInstance,
  InstanceStatus,
} from "@/lib/api/checklist";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { format, isToday } from "date-fns";
import { CommonDestinationTimezone } from "@/lib/utils";
import moment from "moment-timezone";
import { AlertCircle, Clock, Calendar, CheckCircle2, SkipForwardIcon, Workflow, FileText } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { TaskInstanceActionModal } from "./templates/TaskInstanceActionModal";
import { TaskInstanceActionContext } from "./templates/taskInstanceActions/types";
import { hasTaskInstanceAction } from "./templates/taskInstanceActions/registry";
import { TriggerTemplateModal } from "./templates/TriggerTemplateModal";
import { FormData } from "./templates/schemas";

interface MyTasksViewProps {
  onCompleteInstance: (instance: TaskInstance) => void;
  onNonApplInstance: (instance: TaskInstance) => void;
  onViewTask: (taskId: string) => void;
}

export function MyTasksView({
  onCompleteInstance,
  onNonApplInstance,
  onViewTask,
}: MyTasksViewProps) {
  const [daysAheadLook, setDaysAheadLook] = useState(45);
  const [actionInstance, setActionInstance] = useState<TaskInstance | null>(null);
  const [previewInstance, setPreviewInstance] = useState<TaskInstance | null>(null);
  const { member } = useStytchMember();
  const userId = getUserDbId(member);

  const overdueResult = useOverdueInstances();
  const upcomingResult = useUpcomingInstances({ days_ahead: daysAheadLook, assigned_to_id: userId });
  const todayResult = useListInstances({
    status: InstanceStatus.PENDING,
    assigned_to_id: userId,
    due_date_from: moment().startOf("day").toISOString(),
    due_date_to: moment().endOf("day").toISOString(),
  });

  const isLoadingOverdue = overdueResult.isLoading;
  const isLoadingUpcoming = upcomingResult.isLoading;
  const isLoadingToday = todayResult.isLoading;
  
  const overdueInstances = overdueResult?.data?.data || [];
  const upcomingInstances = upcomingResult?.data?.data?.instances || [];
  const todayInstances = todayResult?.data?.data?.instances || [];

  const formatDate = (dateString: string) => {
    return moment.tz(dateString, CommonDestinationTimezone).format("MMM DD, YYYY");
  };

  const formatTime = (dateString: string) => {
    const date = moment.tz(dateString, CommonDestinationTimezone).toDate();
    return format(date, "h:mm a");
  };

  const myOverdueInstances = overdueInstances?.filter(
    (instance: TaskInstance) => instance.assigned_to_id === userId
  ) || [];
  const futureInstances = upcomingInstances?.filter(
    (instance: TaskInstance) => !isToday(new Date(instance.due_date))
  ) || [];

  const buildActionContext = (instance: TaskInstance): TaskInstanceActionContext => ({
    taskInstance: instance,
    taskTitle: instance.task?.title || "",
    templateName: instance.template_instance?.template?.name || "",
  });

  const canShowActionButton = (instance: TaskInstance) =>
        hasTaskInstanceAction(buildActionContext(instance));

  const renderInstanceCard = (instance: TaskInstance) => {
    const showActionButton = canShowActionButton(instance);

    return (
    <Card key={instance.id} className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <button
              onClick={() => onViewTask(instance.task_id)}
              className="text-left font-medium hover:underline gap-4"
            >
              {instance.task?.title}
              <span className="text-muted-foreground text-sm">{instance.template_instance?.trigger_meta_data?.customer_name ? <>&nbsp;{`(${instance.template_instance?.trigger_meta_data?.customer_name})`}</> : ""}</span>
            </button>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Due: {formatDate(instance.due_date)} at{" "}
                {formatTime(instance.due_date)}
              </span>
            </div>
          </div>
          <div className="ml-2 flex flex-col md:flex-row gap-2">
            {showActionButton && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setActionInstance(instance)}
                title="Task Actions"
                className="mr-2"
              >
                <Workflow className="h-4 w-4 mr-1" />
                Actions
              </Button>
            )}

            {instance.template_instance?.trigger_meta_data && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPreviewInstance(instance)}
                title="View Template Form"
                className="mr-2"
              >
                <FileText className="h-4 w-4 mr-1" />
                View Form
              </Button>
            )}

            <Button
              size="sm"
              onClick={() => onNonApplInstance(instance)}
              className="mr-2"
              variant="outline"
              title="non applicable"
            >
              <SkipForwardIcon className="h-4 w-4 mr-1" />
              Mark Non-Appl
            </Button>

            <Button
              size="sm"
              onClick={() => onCompleteInstance(instance)}
              className=""
            >
              <CheckCircle2 className="h-4 w-4 mr-1" />
              Complete
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
  };

  const renderSection = (
    title: string,
    icon: React.ReactNode,
    instances: TaskInstance[] | undefined,
    isLoading: boolean,
    emptyMessage: string,
    cardClassName?: string
  ) => (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {icon}
        <h3 className="text-lg font-semibold">{title}</h3>
        {instances && instances.length > 0 && (
          <Badge variant="secondary">{instances.length}</Badge>
        )}
      </div>
      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : instances && instances.length > 0 ? (
        <div className={`space-y-2 ${cardClassName}`}>
          {instances.map(renderInstanceCard)}
        </div>
      ) : (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            {emptyMessage}
          </CardContent>
        </Card>
      )}
    </div>
  );

  const handleDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 1 && value <= 365) {
      setDaysAheadLook(value);
    }
  };

  return (
    <div className="space-y-8">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overdue</CardTitle>
            <AlertCircle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {myOverdueInstances?.length || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Tasks past due date
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Due Today</CardTitle>
            <Clock className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{todayInstances.length}</div>
            <p className="text-xs text-muted-foreground">
              Tasks due by end of day
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Upcoming</CardTitle>
            <Calendar className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {futureInstances?.length || 0}
            </div>
            <p className="text-xs text-muted-foreground">Tasks in next {daysAheadLook} days</p>
          </CardContent>
        </Card>
      </div>

      {/* Task Sections */}
      {renderSection(
        "Overdue Tasks",
        <AlertCircle className="h-5 w-5 text-destructive" />,
        myOverdueInstances,
        isLoadingOverdue,
        "No overdue tasks",
        "[&>*]:border-destructive"
      )}

      {renderSection(
        "Due Today",
        <Clock className="h-5 w-5 text-primary" />,
        todayInstances,
        isLoadingToday,
        "No tasks due today"
      )}

      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Upcoming Tasks</h3>
          {futureInstances && futureInstances.length > 0 && (
            <Badge variant="secondary">{futureInstances.length}</Badge>
          )}
          <div className="flex items-center gap-2 ml-4">
            <Label htmlFor="days-ahead" className="text-sm">Next</Label>
            <Input
              id="days-ahead"
              type="number"
              min="1"
              max="365"
              value={daysAheadLook}
              onChange={handleDaysChange}
              className="w-16 h-8"
            />
            <span className="text-sm text-muted-foreground">days</span>
          </div>
        </div>
        {isLoadingUpcoming ? (
          <div className="space-y-2">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : futureInstances && futureInstances.length > 0 ? (
          <div className="space-y-2">
            {futureInstances.map(renderInstanceCard)}
          </div>
        ) : (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              {`No upcoming tasks in the next ${daysAheadLook} days`}
            </CardContent>
          </Card>
        )}
      </div>

      {actionInstance && (
        <TaskInstanceActionModal
          open
          onClose={() => setActionInstance(null)}
          taskInstance={actionInstance}
          templateName={actionInstance.template_instance?.template?.name}
        />
      )}

      {previewInstance && previewInstance.template_instance?.trigger_meta_data && (
        <TriggerTemplateModal
          open
          onClose={() => setPreviewInstance(null)}
          template={undefined}
          isPreview={true}
          previewData={previewInstance.template_instance.trigger_meta_data as FormData}
        />
      )}
    </div>
  );
}
